/**
 * RichTextEditor — TipTap wrapper used by:
 *   • Diagram node inspector (edit node body/description)
 *   • Documents module (workspace-level rich-text pages)
 *
 * Props:
 *   value       — current HTML string
 *   onChange    — (html) => void
 *   editable    — bool (default true)
 *   compact     — bool (smaller toolbar/padding)
 *   placeholder — text
 *
 * Feature set (R45 slice 2): headings/bold/italic/lists/links/code + images +
 * tables + slash-commands minimal (headings/lists via toolbar; image via url).
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useMemo } from "react";
import {
  Bold, Italic, List, ListOrdered, Code, Heading1, Heading2, Heading3,
  Link as LinkIcon, Image as ImageIcon, Undo2, Redo2, Quote, Minus,
  Table as TableIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const btnCls = (active) =>
  `h-7 w-7 rounded-none inline-flex items-center justify-center border ${
    active ? "bg-foreground text-background border-foreground" : "bg-background border-neutral-200 hover:border-foreground"
  }`;

export function RichTextEditor({
  value = "",
  onChange,
  editable = true,
  compact = false,
  placeholder = "Type '/' for commands, or just start writing…",
}) {
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: { HTMLAttributes: { class: "bg-neutral-100 border rounded p-2 text-xs font-mono" } },
      // StarterKit v3+ includes Link — disable it so our custom-configured Link below wins.
      link: false,
    }),
    Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-sky-600 underline" } }),
    Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "max-w-full my-2 border" } }),
    Placeholder.configure({ placeholder }),
  ], [placeholder]);

  const editor = useEditor({
    extensions,
    content: value || "",
    editable,
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none ${compact ? "min-h-[80px] px-2 py-1" : "min-h-[160px] px-3 py-2"}`,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    // Keep external `value` in sync when it drifts (e.g. AI rewrite injection)
    if (typeof value === "string" && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;

  const insertImage = () => {
    const url = window.prompt("Image URL (https://…)");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };
  const insertLink = () => {
    const url = window.prompt("Link URL (https://…)", editor.getAttributes("link").href || "");
    if (url === null) return;
    if (!url) return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  };
  const insertTable = () => {
    // Poor-man's table: inject an HTML table (StarterKit doesn't include table by default).
    const rows = Math.max(1, Math.min(10, parseInt(window.prompt("Rows?", "3") || "3", 10)));
    const cols = Math.max(1, Math.min(10, parseInt(window.prompt("Cols?", "3") || "3", 10)));
    let html = "<table border='1' style='border-collapse:collapse;width:100%'><tbody>";
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) html += "<td style='padding:4px;border:1px solid #d4d4d4'>&nbsp;</td>";
      html += "</tr>";
    }
    html += "</tbody></table>";
    editor.chain().focus().insertContent(html).run();
  };

  return (
    <div className="border bg-background" data-testid="rich-text-editor">
      {editable && (
        <div className="flex items-center gap-1 border-b p-1 flex-wrap bg-neutral-50">
          <button type="button" className={btnCls(editor.isActive("bold"))} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" data-testid="rte-bold"><Bold className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("italic"))} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" data-testid="rte-italic"><Italic className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("heading", { level: 1 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="H1"><Heading1 className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("heading", { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="H2"><Heading2 className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("heading", { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="H3"><Heading3 className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("bulletList"))} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><List className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("orderedList"))} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListOrdered className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("blockquote"))} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote"><Quote className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("code"))} onClick={() => editor.chain().focus().toggleCode().run()} title="Inline code"><Code className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(editor.isActive("link"))} onClick={insertLink} title="Link" data-testid="rte-link"><LinkIcon className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(false)} onClick={insertImage} title="Image URL" data-testid="rte-image"><ImageIcon className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(false)} onClick={insertTable} title="Table" data-testid="rte-table"><TableIcon className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(false)} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider"><Minus className="w-3.5 h-3.5" /></button>
          <div className="border-l h-5 mx-1" />
          <button type="button" className={btnCls(false)} onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo2 className="w-3.5 h-3.5" /></button>
          <button type="button" className={btnCls(false)} onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}

/** Read-only inline HTML preview (uses the same prose styles). */
export function RichTextPreview({ html, className = "" }) {
  return (
    <div
      className={`prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: html || "" }}
    />
  );
}
